import React, { memo } from 'react';
import { di } from 'react-magnetic-di';

import Avatar from '@atlaskit/avatar/avatar';
import Skeleton from '@atlaskit/avatar/skeleton';
import type { FileIdentifier } from '@atlaskit/media-client/identifier';
import type { MediaClientConfig } from '@atlaskit/media-client/media-core';
import { MediaProvider } from '@atlaskit/media-client-react/media-provider';
import { MediaImageWithErrorBoundary as MediaImage } from '@atlaskit/media-image/media-image-error-boundary';

import type { CmdbMediaClientConfig } from '../../../../../../services/cmdb-objects/types';

interface CmdbIconProps extends CmdbMediaClientConfig {
	iconUrl?: string;
}

const IMAGE_SIZE = {
	width: 24,
	height: 24,
};

const mediaClientConfigs = new Map<string, MediaClientConfig>();
const getMediaClientConfig = ({
	mediaJwtToken,
	clientId,
	mediaBaseUrl,
}: CmdbMediaClientConfig): MediaClientConfig => {
	const key = `${mediaJwtToken}:${clientId}:${mediaBaseUrl}`;

	const existingMediaClientConfig = mediaClientConfigs.get(key);
	if (existingMediaClientConfig) {
		return existingMediaClientConfig;
	}

	const mediaClientConfig = {
		authProvider: () =>
			Promise.resolve({
				clientId,
				token: mediaJwtToken,
				baseUrl: mediaBaseUrl,
			}),
		useSha256ForUploads: true,
	};
	mediaClientConfigs.set(key, mediaClientConfig);
	return mediaClientConfig;
};

const hasAuth = ({
	mediaBaseUrl,
	mediaJwtToken,
	fileId,
	clientId,
}: CmdbMediaClientConfig): boolean =>
	Boolean(mediaBaseUrl) && Boolean(mediaJwtToken) && Boolean(clientId) && Boolean(fileId);

const DefaultIcon = () => {
	di(Avatar);
	return <Avatar appearance="square" size="small" />;
};

const CmdbIconInternal = (props: CmdbIconProps) => {
	di(MediaProvider, MediaImage, Skeleton, Avatar);
	const { iconUrl } = props;

	if (!hasAuth(props)) {
		if (iconUrl) {
			return <Avatar src={iconUrl} appearance="square" size="small" />;
		}
		return <DefaultIcon />;
	}

	const fileIdentifier: FileIdentifier = {
		id: props.fileId,
		mediaItemType: 'file',
	};

	return (
		<MediaProvider mediaClientConfig={getMediaClientConfig(props)}>
			<MediaImage
				key={props.fileId}
				identifier={fileIdentifier}
				mediaClientConfig={getMediaClientConfig(props)}
				apiConfig={IMAGE_SIZE}
			>
				{({ loading, error, data }) => {
					if (loading) {
						return <Skeleton appearance="square" size="small" />;
					}

					if (error) {
						return <DefaultIcon />;
					}

					if (!data) {
						return <DefaultIcon />;
					}

					return <Avatar src={data.src} appearance="square" size="small" testId="avatar" />;
				}}
			</MediaImage>
		</MediaProvider>
	);
};

export const CmdbIcon = memo(CmdbIconInternal);
